import { directSupabase } from '../lib/supabaseClient';
import {
  draftToContactPatch,
  findDuplicateHints,
  matchesContactSearch,
  rowToContact,
  validateStagedIdentityDraft,
  type ContactDraft,
  type JarvisContact,
  type RecordingContactLink,
} from './contacts.model';
import { findContactsByPhone, findCustomersByPhone, findSuppliersByPhone, type IdentityIndex } from './identity';
import { loadIdentityIndex } from './identity.client';

function missingTable(error: { message?: string; code?: string } | null): boolean {
  const text = String(error?.message || '');
  return error?.code === '42P01' || text.includes('jarvis_contacts') || text.includes('jarvis_contact_recordings');
}

function tableError(): Error {
  const err = new Error('Apply the jarvis_contacts staging SQL, then reload.');
  (err as Error & { code?: string }).code = 'missing_table';
  return err;
}

function withHints(rows: JarvisContact[]): JarvisContact[] {
  return rows.map((row) => ({
    ...row,
    duplicateHints: findDuplicateHints(row, rows),
  }));
}

export async function listJarvisContacts(query = ''): Promise<JarvisContact[]> {
  const { data, error } = await directSupabase
    .from('jarvis_contacts')
    .select('*')
    .is('deleted_at', null)
    .order('display_name', { ascending: true });
  if (error) {
    if (missingTable(error)) throw tableError();
    throw new Error(error.message);
  }
  return withHints((data || []).map((row) => rowToContact(row as Record<string, unknown>)))
    .filter((row) => matchesContactSearch(row, query));
}

export async function listRecordingContactLinks(): Promise<RecordingContactLink[]> {
  const { data, error } = await directSupabase
    .from('jarvis_contact_recordings')
    .select('contact_id, owner_user_id, recording_id');
  if (error) {
    if (missingTable(error)) return [];
    throw new Error(error.message);
  }
  return (data || []).map((row) => ({
    ownerUserId: String(row.owner_user_id),
    recordingId: String(row.recording_id),
    contactId: row.contact_id ? String(row.contact_id) : null,
  }));
}

function duplicateError(index: IdentityIndex, phone: string | null): Error | null {
  const customers = findCustomersByPhone(index.customers, phone);
  if (customers[0]) {
    return new Error(`${customers[0].name} is already in the Directory. A contact was not created.`);
  }
  const suppliers = findSuppliersByPhone(index.suppliers, phone);
  if (suppliers[0]) {
    return new Error(`${suppliers[0].name} is already a supplier. A contact was not created.`);
  }
  const contacts = findContactsByPhone(index.contacts, phone);
  if (contacts[0]) {
    return new Error(`${contacts[0].displayName} already has this number.`);
  }
  return null;
}

export async function createJarvisContact(
  draft: ContactDraft,
  options?: { recording?: { ownerUserId: string; recordingId: string } },
): Promise<{ contact: JarvisContact }> {
  const invalid = validateStagedIdentityDraft(draft);
  if (invalid) throw new Error(invalid);
  const patch = draftToContactPatch(draft);
  const index = await loadIdentityIndex();
  const blocked = duplicateError(index, patch.phone_normalized || patch.phone_original || patch.landline_normalized);
  if (blocked) throw blocked;

  const { data: sessionData } = await directSupabase.auth.getSession();
  const insert: Record<string, unknown> = {
    ...patch,
    source: 'local',
    kind: 'person',
    status: 'staged',
    created_by: sessionData.session?.user.id || null,
  };

  let { data, error } = await directSupabase
    .from('jarvis_contacts')
    .insert(insert)
    .select('*')
    .single();

  if (error && /status|customer_id/i.test(error.message || '')) {
    delete insert.status;
    const retry = await directSupabase.from('jarvis_contacts').insert(insert).select('*').single();
    data = retry.data;
    error = retry.error;
  }
  if (error) {
    if (missingTable(error)) throw tableError();
    throw new Error(error.message);
  }

  const contact = rowToContact(data as Record<string, unknown>);
  if (options?.recording) {
    await linkRecordingToContact(contact.id, options.recording.ownerUserId, options.recording.recordingId);
  }
  return { contact };
}

export async function updateJarvisContact(id: string, draft: ContactDraft): Promise<{ contact: JarvisContact }> {
  const invalid = validateStagedIdentityDraft(draft);
  if (invalid) throw new Error(invalid);
  const patch = draftToContactPatch(draft);
  const index = await loadIdentityIndex();
  const customers = findCustomersByPhone(index.customers, patch.phone_normalized || patch.phone_original);
  if (customers[0]) {
    const existing = index.contacts.find((row) => row.id === id);
    if (!existing?.customerId || existing.customerId !== customers[0].id) {
      throw new Error(`${customers[0].name} is already in the Directory for this number.`);
    }
  }
  const suppliers = findSuppliersByPhone(index.suppliers, patch.phone_normalized || patch.phone_original);
  if (suppliers[0]) {
    throw new Error(`${suppliers[0].name} is already a supplier for this number.`);
  }
  const other = findContactsByPhone(index.contacts, patch.phone_normalized || patch.phone_original)
    .find((row) => row.id !== id);
  if (other) throw new Error(`${other.displayName} already has this number.`);

  const { data, error } = await directSupabase
    .from('jarvis_contacts')
    .update({
      ...patch,
      local_edited_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return { contact: rowToContact(data as Record<string, unknown>) };
}

export async function deleteJarvisContact(id: string): Promise<void> {
  const { error } = await directSupabase
    .from('jarvis_contacts')
    .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/** Links the contact to its Directory customer and removes it from the Contacts list. */
export async function promoteJarvisContact(contactId: string, customerId: string): Promise<JarvisContact> {
  const now = new Date().toISOString();
  const { data, error } = await directSupabase
    .from('jarvis_contacts')
    .update({
      customer_id: customerId,
      status: 'promoted',
      deleted_at: now,
      updated_at: now,
    })
    .eq('id', contactId)
    .select('*')
    .single();
  if (error) {
    if (/customer_id|status/i.test(error.message || '')) {
      throw new Error('Apply the jarvis_contacts staging SQL so contacts can be linked to Directory customers.');
    }
    throw new Error(error.message);
  }
  return rowToContact(data as Record<string, unknown>);
}

export async function linkRecordingToContact(contactId: string, ownerUserId: string, recordingId: string): Promise<void> {
  const { error } = await directSupabase
    .from('jarvis_contact_recordings')
    .upsert({ contact_id: contactId, owner_user_id: ownerUserId, recording_id: recordingId }, { onConflict: 'owner_user_id,recording_id' });
  if (error && !missingTable(error)) throw new Error(error.message);
}

export async function matchesForPhone(phone: string | null) {
  const index = await loadIdentityIndex();
  return {
    customers: findCustomersByPhone(index.customers, phone),
    contacts: findContactsByPhone(index.contacts, phone),
    index,
  };
}
